import { getUser } from "@web/api"
import { LoginForm, LoginFormProps } from "@web/components/Auth/LoginForm"
import { useAuthState } from "@web/hooks/useAuthState"
import { GetServerSideProps, NextPage } from "next"
import { getServerSession } from "next-auth"
import { useRouter } from "next/router"
import * as React from "react"
import { authOptionsBase } from "./api/auth/[...nextauth]"
import { AuthLayout } from "@web/components/Auth/Layout"
import { Alert, LinearProgress } from "@mui/material"
import MuiThemeProvider from "@web/components/Forms/MuiThemeProvider"
import { useAfterLoginHooks } from "@web/hooks/useAfterLoginHooks"
import { APP_DISPLAY_NAME } from "platform-common/index"

interface Props {
  /**
   * Where to redirect the user after login
   */
  redirectTo?: string | null
}

const LoginPage: NextPage = ({ redirectTo }: Props) => {
  const router = useRouter()
  const autoLogin = router.query.autoLogin === "true"

  const { user } = useAuthState()
  const [loginConfig, setLoginConfig] = React.useState<LoginFormProps>({
    title: `Sign In To ${APP_DISPLAY_NAME}`,
  })

  React.useEffect(() => {
    setLoginConfig((c) => ({ ...c, autoLogin }))
  }, [autoLogin])

  useAfterLoginHooks({ redirectTo })

  if (user) {
    return (
      <AuthLayout>
        <div>
          <Alert severity="success">You will be redirected in a few seconds.</Alert>
          <MuiThemeProvider>
            <LinearProgress color="secondary" />
          </MuiThemeProvider>
        </div>
      </AuthLayout>
    )
  }

  return <LoginForm {...loginConfig} />
}

export default LoginPage

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const session = await getServerSession(context.req, context.res, authOptionsBase)

  if (!session) return { props: {} }

  const userResponse = await getUser(context)
  const user = userResponse.data || null
  const shouldOnboard = !user?.tosAcceptedAt
  const shouldRedirectToChat = context.query.chat === "true" // AI chat doesn't need onboarding
  const callbackUrl = (context.query.callbackUrl as string) ?? null // where was the user trying to go before being redirected to login

  let redirectTo: string | null = null

  if (shouldRedirectToChat) {
    redirectTo = "/ai/chat"
  } else if (shouldOnboard) {
    redirectTo = "/onboarding"

    if (callbackUrl) {
      // pass the callbackUrl to the onboarding page to redirect the user back to the original page after onboarding
      redirectTo += `?redirectTo=${encodeURIComponent(callbackUrl)}`
    }
  }

  return { props: { redirectTo } }
}
